class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.mobileScrollPosition = null;
    this.mobileCloseTimer = null;
    this.closeForHeaderAction = this.closeForHeaderAction.bind(this);
    document.addEventListener('click', this.closeForHeaderAction, true);

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (this.mobileCloseTimer !== null) {
      clearTimeout(this.mobileCloseTimer);
      this.mobileCloseTimer = null;
    }
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    this.lockMobilePageScroll();
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
      document.body.classList.remove('demyr-cart-closing');
    });

    this.addEventListener(
      'transitionend',
      () => {
        if (!this.classList.contains('active')) return;
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    if (this.mobileScrollPosition === null) document.body.classList.add('overflow-hidden');
  }

  closeForHeaderAction(event) {
    if (!this.classList.contains('active')) return;
    if (!event.target.closest('.section-header, header')) return;
    if (event.target.closest('#cart-icon-bubble')) return;
    this.close(false, true);
  }

  close(restoreFocus = true, immediateUnlock = false) {
    const mobileWasLocked = this.mobileScrollPosition !== null;
    if (this.mobileCloseTimer !== null) clearTimeout(this.mobileCloseTimer);
    if (mobileWasLocked) document.body.classList.add('demyr-cart-closing');
    this.classList.remove('active');
    removeTrapFocus(restoreFocus ? this.activeElement : undefined);
    if (mobileWasLocked && immediateUnlock) {
      document.body.classList.remove('overflow-hidden');
      this.unlockMobilePageScroll();
      this.mobileCloseTimer = setTimeout(() => {
        document.body.classList.remove('demyr-cart-closing');
        this.mobileCloseTimer = null;
      }, 440);
      return;
    }
    if (mobileWasLocked) {
      this.mobileCloseTimer = setTimeout(() => {
        document.body.classList.remove('demyr-cart-closing', 'overflow-hidden');
        this.unlockMobilePageScroll();
        this.mobileCloseTimer = null;
      }, 440);
    } else {
      document.body.classList.remove('overflow-hidden');
      this.unlockMobilePageScroll();
    }
  }

  lockMobilePageScroll() {
    if (!window.matchMedia('(max-width: 749px)').matches || this.mobileScrollPosition !== null) return;

    this.mobileScrollPosition = window.scrollY;
    const headerBottom = document.querySelector('.section-header')?.getBoundingClientRect().bottom || 0;
    document.body.style.setProperty('--demyr-cart-header-bottom', `${Math.max(0, Math.round(headerBottom))}px`);
    window.DemyrMobilePageLock.lock(this);
  }

  unlockMobilePageScroll() {
    if (this.mobileScrollPosition === null) return;

    this.mobileScrollPosition = null;
    document.body.style.removeProperty('--demyr-cart-header-bottom');
    window.DemyrMobilePageLock.unlock(this);
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);